// apps/vendor/src/pages/settings/NotificationChannelsSection.tsx
//
// Plan 4 Task 11 — vendor Profile → Notification channels section.
// Mirrors the merchant Task 9 logic but uses the vendor-local `csrfFetch`
// helper (double-submit CSRF + credentials) instead of the merchant
// `request()` client. Endpoints are user-scoped so the same paths work
// across merchant/admin/vendor:
//   GET    /api/v1/me/notification-channels
//   POST   /api/v1/me/notification-channels/telegram/link  → { deepLink }
//   POST   /api/v1/me/notification-channels/lark/connect   → { connected }
//   PATCH  /api/v1/me/notification-channels/:id            (enable/disable)
//   DELETE /api/v1/me/notification-channels/:id            (disconnect)
//
// All BE responses use the `{ ok, data | error }` envelope (same as every
// other vendor feature api.ts), so we unwrap here.

import type { JSX } from 'react';
import { useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useToast } from '@printway/ui';

import { csrfFetch } from '../../lib/api/csrfFetch';

interface ChannelView {
  id: string;
  channel: 'lark' | 'telegram';
  enabled: boolean;
  connected: boolean;
}
interface ListResp {
  channels: ChannelView[];
}

interface ApiOk<T> {
  ok: true;
  data: T;
}
interface ApiErr {
  ok: false;
  error: { code: string; message: string };
}

class NotifChannelsApiError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  constructor(code: string, message: string, statusCode: number) {
    super(message);
    this.name = 'NotifChannelsApiError';
    this.code = code;
    this.statusCode = statusCode;
  }
}

async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => null)) as
    | ApiOk<T>
    | ApiErr
    | null;
  if (!body) {
    throw new NotifChannelsApiError('NETWORK', 'Empty body', res.status);
  }
  if (!body.ok) {
    throw new NotifChannelsApiError(
      body.error.code,
      body.error.message,
      res.status,
    );
  }
  return body.data;
}

const BASE = '/api/v1/me/notification-channels';

async function listChannels(): Promise<ListResp> {
  return unwrap<ListResp>(await csrfFetch(BASE));
}
async function telegramLink(): Promise<{ deepLink: string }> {
  return unwrap<{ deepLink: string }>(
    await csrfFetch(`${BASE}/telegram/link`, { method: 'POST' }),
  );
}
async function larkConnect(webhookUrl: string): Promise<{ connected: boolean }> {
  return unwrap<{ connected: boolean }>(
    await csrfFetch(`${BASE}/lark/connect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ webhookUrl }),
    }),
  );
}
async function toggleChannel(id: string, enabled: boolean): Promise<unknown> {
  return unwrap<unknown>(
    await csrfFetch(`${BASE}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    }),
  );
}
async function disconnectChannel(id: string): Promise<unknown> {
  return unwrap<unknown>(await csrfFetch(`${BASE}/${id}`, { method: 'DELETE' }));
}

export function NotificationChannelsSection(): JSX.Element {
  const { t } = useTranslation('notifChannels');
  const { toast } = useToast();
  const qc = useQueryClient();
  const [larkUrl, setLarkUrl] = useState('');

  const { data, isError } = useQuery<ListResp, NotifChannelsApiError>({
    queryKey: ['vendor', 'notification-channels'],
    queryFn: listChannels,
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({
      queryKey: ['vendor', 'notification-channels'],
    });
  };

  // fix/vendor-batch-e — every mutation surfaces its failure (silent-mutation
  // sweep): server rejections were previously swallowed, reading as "nothing
  // happened" in the UI.
  const mutError = (title: string) => (e: unknown): void => {
    toast({
      variant: 'error',
      title,
      description: e instanceof Error ? e.message : undefined,
    });
  };

  const telegramLinkMut = useMutation({
    mutationFn: telegramLink,
    onSuccess: (r) => window.open(r.deepLink, '_blank', 'noopener'),
    onError: mutError(t('error.generic')),
  });
  const larkConnectMut = useMutation({
    mutationFn: () => larkConnect(larkUrl),
    onSuccess: () => {
      setLarkUrl('');
      invalidate();
    },
    onError: mutError(t('error.lark_verify_failed')),
  });
  const toggleMut = useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) =>
      toggleChannel(v.id, v.enabled),
    onSuccess: invalidate,
    onError: mutError(t('error.generic')),
  });
  const disconnectMut = useMutation({
    mutationFn: (id: string) => disconnectChannel(id),
    onSuccess: invalidate,
    onError: mutError(t('error.generic')),
  });

  const byKind = (k: 'lark' | 'telegram'): ChannelView | undefined =>
    data?.channels.find((c) => c.channel === k);
  const tg = byKind('telegram');
  const lk = byKind('lark');

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t('title')}</h2>
        <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
      </div>
      {isError && <p className="text-sm text-destructive">{t('error.load')}</p>}

      {/* In-app + Email: always on (informational) */}
      <div className="flex items-center justify-between rounded-md border p-3">
        <span>{t('channel.in_app')}</span>
        <span className="text-sm text-muted-foreground">
          {t('status.always_on')}
        </span>
      </div>
      <div className="flex items-center justify-between rounded-md border p-3">
        <span>{t('channel.email')}</span>
        <span className="text-sm text-muted-foreground">
          {t('status.always_on')}
        </span>
      </div>

      {/* Telegram */}
      <div className="flex items-center justify-between rounded-md border p-3">
        <span>{t('channel.telegram')}</span>
        {tg?.connected ? (
          <div className="flex gap-2">
            <button
              type="button"
              className="text-sm underline"
              onClick={() =>
                toggleMut.mutate({ id: tg.id, enabled: !tg.enabled })
              }
            >
              {tg.enabled ? t('action.disable') : t('action.enable')}
            </button>
            <button
              type="button"
              className="text-sm text-destructive underline"
              onClick={() => disconnectMut.mutate(tg.id)}
            >
              {t('action.disconnect')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="text-sm underline"
            onClick={() => telegramLinkMut.mutate()}
          >
            {t('action.connect')}
          </button>
        )}
      </div>
      {!tg?.connected && telegramLinkMut.data && (
        <p className="text-xs text-muted-foreground">
          {t('telegram.instructions')}
        </p>
      )}

      {/* Lark */}
      <div className="space-y-2 rounded-md border p-3">
        <div className="flex items-center justify-between">
          <span>{t('channel.lark')}</span>
          {lk?.connected && (
            <button
              type="button"
              className="text-sm text-destructive underline"
              onClick={() => disconnectMut.mutate(lk.id)}
            >
              {t('action.disconnect')}
            </button>
          )}
        </div>
        {!lk?.connected && (
          <div className="space-y-2">
            <label className="block text-sm" htmlFor="lark-webhook-url">
              {t('lark.webhook_label')}
            </label>
            <input
              id="lark-webhook-url"
              className="w-full rounded border px-2 py-1 text-sm"
              placeholder={t('lark.webhook_placeholder')}
              value={larkUrl}
              onChange={(e) => setLarkUrl(e.target.value)}
            />
            <button
              type="button"
              className="text-sm underline"
              disabled={!larkUrl}
              onClick={() => larkConnectMut.mutate()}
            >
              {t('lark.connect')}
            </button>
            {larkConnectMut.isError && (
              <p className="text-sm text-destructive">
                {t('error.lark_verify_failed')}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
